/* 패턴 상세 모달 — 차트분석 탭의 감지된 패턴 카드 클릭 시 표시.
 *  사전 페이지(/chart-patterns)로 이동하지 않고 그 자리에서 패턴 상세를 보여준다.
 *  내용: SVG(크게) + 패턴명 한글(영문) + 방향 뱃지 + AI 설명 + 패턴 설명 + 통계기반 달성률 + 사전 링크. */
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useScrollLock } from '@/hooks/useScrollLock';
import { cn } from '@/lib/cn';
import { findPatternByFile } from '@/data/chartPatterns';
import {
  PatternImg,
  DirBadge,
  patternAnchor,
  SuccessRateBar,
  type PatternDir,
} from './patternGraphics';

export interface PatternDetailModalProps {
  open: boolean;
  onClose: () => void;
  /** classifyPattern 결과 file. 미분류시 빈 문자열 → 사전 lookup 실패하면 AI 정보만 표시 */
  file: string;
  /** AI 가 감지한 패턴 원본 정보(이름·설명) */
  ai: {
    name: string;
    description?: string;
  };
  /** classifyPattern 으로 추정한 방향 */
  dir: PatternDir;
}

export function PatternDetailModal({ open, onClose, file, ai, dir }: PatternDetailModalProps) {
  useScrollLock(open);

  /* ESC 키로 닫기 */
  useEffect(() => {
    if (!open) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [open, onClose]);

  if (!open) return null;

  const def = findPatternByFile(file);
  // 표시명: 사전에 있으면 사전 이름(= "한글 (영문)"), 없으면 AI 원본 이름
  const displayName = def?.name ?? ai.name;
  const anchor = patternAnchor(file);
  const dictHref = `/chart-patterns${anchor ? `#${anchor}` : ''}`;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={displayName}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="relative max-h-[90vh] w-full max-w-lg overflow-hidden rounded-2xl bg-[var(--color-surface)] shadow-xl">
        {/* 닫기 버튼 */}
        <button
          type="button"
          onClick={onClose}
          aria-label="닫기"
          className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white/80 text-[var(--color-text-sub)] shadow-sm transition-colors hover:bg-white hover:text-[var(--color-text)]"
        >
          ✕
        </button>

        <div className="max-h-[90vh] overflow-y-auto">
          {/* SVG 도식 (크게) — 다크 원본 그대로 */}
          <div className="bg-[#111]">
            <PatternImg file={file} name={displayName} className="block h-auto w-full" />
          </div>

          {/* 본문 */}
          <div className="space-y-4 p-5">
            {/* 패턴명 + 방향 뱃지 */}
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-extrabold text-[var(--color-text)]">{displayName}</h2>
              <DirBadge dir={dir} />
            </div>

            {/* AI 분석 설명 — 이 종목에서 감지된 구체적 근거 */}
            {ai.description && (
              <section>
                <h3 className="mb-1.5 text-xs font-bold text-[var(--color-text-sub)]">AI 분석 근거</h3>
                <p className="whitespace-pre-line rounded-lg bg-[var(--color-bg)] p-3 text-sm leading-relaxed text-[var(--color-text)]">
                  {ai.description}
                </p>
              </section>
            )}

            {/* 패턴 일반 설명 — 사전에 있을 때만 */}
            {def?.desc && (
              <section>
                <h3 className="mb-1.5 text-xs font-bold text-[var(--color-text-sub)]">패턴 설명</h3>
                <p className="text-sm leading-relaxed text-[var(--color-text)]">{def.desc}</p>
              </section>
            )}

            {/* 통계기반 달성률 — 있을 때만 */}
            {def?.successRate != null && (
              <section>
                <h3 className="mb-1.5 text-xs font-bold text-[var(--color-text-sub)]">통계기반 달성률</h3>
                <SuccessRateBar rate={def.successRate} />
                <p className="mt-1.5 text-[10px] leading-relaxed text-[var(--color-text-sub)]">
                  ※ 통계기반 달성률은 Thomas Bulkowski의 차트 패턴 백테스트 통계 기반이며, 실제 수익을 보장하지 않습니다.
                </p>
              </section>
            )}

            {/* 사전 페이지 링크 */}
            <div className="flex justify-end pt-1">
              <Link
                to={dictHref}
                onClick={onClose}
                className={cn(
                  'inline-flex items-center gap-1 text-sm font-semibold text-[var(--color-primary)] hover:underline',
                )}
              >
                차트 패턴 사전에서 더 보기 →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
